/**
 * The select tool, from the hand going down to the board being changed (SRS C4).
 *
 * Two ways of choosing work — a freehand loop and a dragged box — and then move,
 * resize, turn, delete, duplicate and recolour whatever was chosen.
 *
 * A drag is always worked out from the strokes as they were when it began, not from
 * where they are now. Applying a small change over and over would let rounding creep
 * into the writing; applying one whole change to the original never does.
 *
 * The board on this screen changes as the hand moves. The shared copy is written once,
 * when the hand lifts, so a whole drag is a single press of undo (SRS C6).
 */

import type { BoardObject, Point, Rect, Stroke, Unsubscribe } from '@opal/shared';

import { isEmptyEdit, NO_EDIT, type BoardEdit } from './board-edit.js';
import { boundsOfPoints, growRect, rectFromCorners, translation, type Matrix } from './geometry.js';
import { clipboardActions, type ClipboardActions } from './board-clipboard.js';
import {
  DUPLICATE_OFFSET,
  grabReach,
  handleAt,
  handlesFor,
  recolourStroke,
  resizeMatrix,
  rotateMatrix,
  selectionBounds,
  strokeInPolygon,
  strokeInRect,
  transformStroke,
  type Copied,
  type Duplicated,
  type HandleId,
  type SelectMode,
} from './selection.js';
import { forgetIds, groupActions, isGrouped, withGroups, type Grouping } from './select-groups.js';
import { strokeBounds } from './stroke-bounds.js';
import type { ZMove } from './z-order.js';

export type { Duplicated } from './selection.js';

/** The part of the board the select tool works on. The ink session provides it. */
export interface SelectableInk {
  near(area: Rect): readonly Stroke[];
  /** The stroke with this id, for picking up the rest of a group. */
  find(strokeId: string): Stroke | null;
  remove(strokeId: string): boolean;
  accept(stroke: Stroke): void;
  replace(stroke: Stroke): boolean;
  /** A second copy of one piece of the board, offset, and already on the board. */
  duplicate(stroke: Stroke, offset: number): Duplicated | null;
  /** §16 — one piece of the board, lifted off it for the clipboard to hold. */
  copyOf(strokeId: string): Copied | null;
  /** §16 — a held copy put back on the board, offset. */
  place(item: Copied, offset: number): Duplicated | null;
  /** Everything on the page, for Ctrl+A. */
  all(): readonly Stroke[];
  /** SRS C8 — sends the objects among these forward or back, and says which moved. */
  reorder(strokeIds: readonly string[], move: ZMove): readonly BoardObject[];
}

export interface SelectSessionOptions {
  readonly ink: SelectableInk;
  /** Lasso or box. Read when the hand goes down and held for that gesture. */
  readonly mode: () => SelectMode;
  /** How far the board is zoomed, so grips are the same size on screen at any zoom. */
  readonly zoom: () => number;
}

/** What the properties panel can do to a selection. */
export interface SelectionActions {
  /** SRS C4 — takes the selected work off the board. */
  remove(): void;
  /** SRS C4 — a second copy, offset so it is visibly a copy. */
  duplicate(): void;
  /** SRS C4 — the selected work in a different colour. */
  setColor(color: string): void;
  /**
   * SRS C8 — sends the selected work forward or back within its own layer.
   *
   * Only the shapes, text boxes and sticky notes among it move: ink has no order of
   * its own beyond the order it was drawn in.
   */
  reorder(move: ZMove): void;
  /**
   * SRS C4 — keeps the chosen work together, so touching any of it takes all of it.
   *
   * The grouping lasts for as long as the board is open. It is never written to the
   * board itself, so nothing about the way a lecture is stored changes, and reopening
   * one gives back exactly the strokes that were drawn.
   */
  group(): void;
  /** Lets a group go, leaving the work exactly where it is. */
  ungroup(): void;
  /** docs/06-UI-SPEC.md §16 — remembers the selected work, leaving it where it is. */
  copy(): void;
  /** §16 — remembers it and takes it off the board. */
  cut(): void;
  /** §16 — puts a copy of whatever was last copied or cut on the board. */
  paste(): void;
  /** §16 — chooses everything on this page. */
  selectAll(): void;
  /** Nothing selected any more. */
  clear(): void;
}

export interface SelectSession extends SelectionActions {
  /** True when what is chosen belongs to a group. */
  readonly grouped: boolean;
  /** §16 — how many pieces the clipboard holds, so the panel can offer Paste. */
  readonly copied: number;
  begin(at: Point): void;
  extend(points: readonly Point[]): void;
  finish(): void;
  /** The gesture was taken away: a drag goes back to where it started. */
  cancel(): void;
  /** Called with each finished change, for sharing and saving. */
  onEdited(handler: (edit: BoardEdit) => void): Unsubscribe;
  /** Called whenever what is selected, or where it is, changes. */
  onChanged(handler: () => void): Unsubscribe;
  readonly selection: readonly Stroke[];
  /** The loop or box being drawn right now, for the overlay. Null when there is none. */
  readonly marquee: readonly Point[] | null;
  bounds(): Rect | null;
}

/** Choosing work: the hand is drawing a loop or a box. */
interface Marquee {
  readonly kind: 'marquee';
  readonly mode: SelectMode;
  readonly path: Point[];
}

/** Changing work: the hand is moving, resizing or turning what is already chosen. */
interface Transform {
  readonly kind: 'move' | 'resize' | 'rotate';
  readonly handle: HandleId | null;
  readonly from: Point;
  /** The strokes as they were when the drag began. Every frame works from these. */
  readonly originals: readonly Stroke[];
  readonly bounds: Rect;
}

type Drag = Marquee | Transform;

interface State extends Grouping {
  readonly options: SelectSessionOptions;
  readonly editHandlers: Set<(edit: BoardEdit) => void>;
  readonly changeHandlers: Set<() => void>;
  drag: Drag | null;
}

function publish(state: State, edit: BoardEdit): void {
  if (isEmptyEdit(edit)) return;
  for (const handler of state.editHandlers) handler(edit);
}

function boundsOf(state: State): Rect | null {
  return selectionBounds(state.selected, strokeBounds);
}

/** Puts a changed version of the selection on this screen, and remembers it. */
function show(state: State, strokes: readonly Stroke[]): void {
  state.selected = [...strokes];
  for (const stroke of strokes) state.options.ink.replace(stroke);
  state.announce();
}

/** The box a loop or a dragged box covers. */
function areaOf(drag: Marquee): Rect | null {
  if (drag.mode !== 'rect') return boundsOfPoints(drag.path);
  const first = drag.path[0];
  const last = drag.path[drag.path.length - 1];
  return first && last ? rectFromCorners(first, last) : null;
}

/**
 * The strokes a finished loop or box has caught.
 *
 * Anything the shape touches counts, not only what it swallows whole: a teacher
 * lassoing a diagram should not have to enclose every last tail of every letter.
 */
function caught(state: State, drag: Marquee): Stroke[] {
  const area = areaOf(drag);
  if (!area) return [];
  const nearby = state.options.ink.near(area);
  return drag.mode === 'rect'
    ? nearby.filter((stroke) => strokeInRect(stroke, area))
    : nearby.filter((stroke) => strokeInPolygon(stroke, drag.path));
}

/** What kind of drag the hand has started, given where it went down. */
function dragFrom(state: State, at: Point): Drag {
  const bounds = boundsOf(state);
  const zoom = state.options.zoom();
  if (bounds) {
    const reach = grabReach(bounds, zoom);
    const handle = handleAt(at, handlesFor(bounds, zoom), reach);
    const originals = [...state.selected];
    if (handle === 'rotate') return { kind: 'rotate', handle, from: at, originals, bounds };
    if (handle) return { kind: 'resize', handle, from: at, originals, bounds };
    // Inside the box, but not on a grip: take hold of the work and move it.
    if (pointInside(at, bounds, reach)) {
      return { kind: 'move', handle: null, from: at, originals, bounds };
    }
  }
  return { kind: 'marquee', mode: state.options.mode(), path: [at] };
}

/** Whether a point counts as inside the selection box, with a margin for a finger. */
function pointInside(point: Point, bounds: Rect, reach: number): boolean {
  const box = growRect(bounds, reach / 2);
  return (
    point.x >= box.x &&
    point.x <= box.x + box.width &&
    point.y >= box.y &&
    point.y <= box.y + box.height
  );
}

/** The change a drag describes, from where it started to where the hand is now. */
function matrixFor(drag: Transform, to: Point): Matrix {
  if (drag.kind === 'move') return translation(to.x - drag.from.x, to.y - drag.from.y);
  if (drag.kind === 'rotate') return rotateMatrix(drag.bounds, drag.from, to);
  return resizeMatrix(drag.bounds, drag.handle ?? 'se', drag.from, to);
}

/** Draws the drag on this screen, worked out from where the strokes started. */
function dragTo(state: State, drag: Transform, to: Point): void {
  const matrix = matrixFor(drag, to);
  show(
    state,
    drag.originals.map((stroke) => transformStroke(stroke, matrix)),
  );
}

function finishMarquee(state: State, drag: Marquee): void {
  state.selected = withGroups(state, caught(state, drag));
  state.announce();
}

/**
 * Shares what the drag changed.
 *
 * A drag that never moved leaves the very strokes it started with, and those are not
 * worth sending: a click inside the selection should not become a step of undo.
 */
function finishTransform(state: State, drag: Transform): void {
  const untouched = new Set(drag.originals);
  publish(state, {
    ...NO_EDIT,
    replaced: state.selected.filter((stroke) => !untouched.has(stroke)),
  });
}

/** SRS C8 — sends the objects among the selection forward or back within their layer. */
function reorder(state: State, move: ZMove): void {
  const moved = state.options.ink.reorder(
    state.selected.map((stroke) => stroke.id),
    move,
  );
  if (moved.length === 0) return;
  state.announce();
  publish(state, { ...NO_EDIT, objectsChanged: [...moved] });
}

/** Everything else the properties panel does to a selection. */
function actions(
  state: State,
): Pick<SelectionActions, 'remove' | 'duplicate' | 'setColor' | 'reorder'> {
  return {
    remove: () => {
      const removed = state.selected
        .filter((stroke) => state.options.ink.remove(stroke.id))
        .map((stroke) => stroke.id);
      state.selected = [];
      forgetIds(state, removed);
      state.announce();
      publish(state, { ...NO_EDIT, removed });
    },

    duplicate: () => {
      const wasGrouped = isGrouped(state);
      const copies = state.selected
        .map((stroke) => state.options.ink.duplicate(stroke, DUPLICATE_OFFSET))
        .filter((copy): copy is Duplicated => copy !== null);
      const views = copies.map((copy) => copy.view);
      if (views.length === 0) return;
      // A copy of grouped work is grouped too, or it would fall apart when moved.
      if (wasGrouped && views.length > 1) {
        state.groups.push(new Set(views.map((view) => view.id)));
      }
      // The copy is what is now selected, so it can be dragged straight into place.
      state.selected = views;
      state.announce();
      publish(state, {
        ...NO_EDIT,
        added: copies.flatMap((copy) => (copy.stroke ? [copy.stroke] : [])),
        objectsAdded: copies.flatMap((copy) => (copy.object ? [copy.object] : [])),
      });
    },

    setColor: (color) => {
      const recoloured = state.selected
        .filter((stroke) => stroke.color !== color)
        .map((stroke) => recolourStroke(stroke, color));
      if (recoloured.length === 0) return;
      const byId = new Map(recoloured.map((stroke) => [stroke.id, stroke]));
      show(
        state,
        state.selected.map((stroke) => byId.get(stroke.id) ?? stroke),
      );
      publish(state, { ...NO_EDIT, replaced: recoloured });
    },

    reorder: (move) => {
      reorder(state, move);
    },
  };
}

/**
 * What the tool tells the rest of the screen about itself.
 *
 * Plain functions only. A getter here would be read once as this is folded into the
 * session and its answer frozen for good — which is exactly the kind of fault that
 * leaves a selection box drawn around work that has already been let go.
 */
function reporting(state: State): Pick<SelectSession, 'bounds' | 'onEdited' | 'onChanged'> {
  return {
    bounds: () => boundsOf(state),

    onEdited(handler) {
      state.editHandlers.add(handler);
      return () => {
        state.editHandlers.delete(handler);
      };
    },

    onChanged(handler) {
      state.changeHandlers.add(handler);
      return () => {
        state.changeHandlers.delete(handler);
      };
    },
  };
}

/** The hand going down, moving and lifting. */
function gestures(state: State): Pick<SelectSession, 'begin' | 'extend' | 'finish' | 'cancel'> {
  return {
    begin(at) {
      state.drag = dragFrom(state, at);
      state.announce();
    },

    extend(points) {
      const drag = state.drag;
      const last = points[points.length - 1];
      if (!drag || !last) return;
      if (drag.kind !== 'marquee') {
        dragTo(state, drag, last);
        return;
      }
      // A box only ever needs the two corners; a loop needs every point of it.
      if (drag.mode === 'rect' && drag.path.length > 1) drag.path[1] = last;
      else drag.path.push(...points);
      state.announce();
    },

    finish() {
      const drag = state.drag;
      state.drag = null;
      if (!drag) return;
      if (drag.kind === 'marquee') finishMarquee(state, drag);
      else finishTransform(state, drag);
      state.announce();
    },

    cancel() {
      const drag = state.drag;
      state.drag = null;
      // A drag that was taken away leaves the work exactly where it was, rather than
      // committing half a movement nobody asked for.
      if (drag && drag.kind !== 'marquee') show(state, drag.originals);
      state.announce();
    },
  };
}

/**
 * §16 — how the clipboard sees this tool.
 *
 * It reads what is chosen, hands back the copies a paste made, and calls the tool's
 * own delete for the second half of a cut.
 */
function clipboardFor(state: State, remove: () => void): ClipboardActions {
  return clipboardActions({
    board: state.options.ink,
    selected: () => state.selected,
    remove,
    hold: (views, edit) => {
      // The copy is what is now selected, so it can be dragged straight into place.
      state.selected = [...views];
      state.announce();
      publish(state, edit);
    },
    announce: () => {
      state.announce();
    },
  });
}

/** A select tool with nothing chosen and nobody listening yet. */
function emptyState(options: SelectSessionOptions): State {
  const state: State = {
    options,
    ink: options.ink,
    editHandlers: new Set<(edit: BoardEdit) => void>(),
    changeHandlers: new Set<() => void>(),
    selected: [],
    drag: null,
    groups: [],
    announce: () => {
      for (const handler of state.changeHandlers) handler();
    },
    onClear: () => {
      state.drag = null;
    },
  };
  return state;
}

export function createSelectSession(options: SelectSessionOptions): SelectSession {
  const state = emptyState(options);
  const selectionActions = actions(state);
  const clipboard = clipboardFor(state, () => {
    selectionActions.remove();
  });

  return {
    ...selectionActions,
    ...groupActions(state),
    ...reporting(state),
    copy: () => {
      clipboard.copy();
    },
    cut: () => {
      clipboard.cut();
    },
    paste: () => {
      clipboard.paste();
    },

    /** §16 — everything on this page, whatever it is and wherever it sits. */
    selectAll: () => {
      state.selected = [...options.ink.all()];
      state.announce();
    },

    get grouped() {
      return isGrouped(state);
    },

    get copied() {
      return clipboard.held;
    },

    get selection() {
      return state.selected;
    },

    get marquee() {
      return state.drag?.kind === 'marquee' ? state.drag.path : null;
    },

    ...gestures(state),
  };
}
